<template>
  <!-- ICONIC: eine Person, die gerade online ist -->
  <v-card outlined>
    <template v-if="person">
      <v-card-title>
        <span class="text-truncate">{{ person.clientNickname }}</span>
        <v-icon v-if="locked" color="warning" class="ml-2">mdi-lock</v-icon>
        <v-spacer></v-spacer>
        <v-btn icon small aria-label="Schließen" @click="$emit('close')"
          ><v-icon>mdi-close</v-icon></v-btn
        >
      </v-card-title>
      <v-card-subtitle class="pb-2">
        {{ status }} · {{ onlineSince }} · in
        <a class="channel-link" @click="$emit('select-channel', person.cid)">{{
          channelName
        }}</a>
      </v-card-subtitle>
      <v-card-text class="pb-2">
        <div class="mb-3">
          <v-chip small class="mr-1 mb-1" :color="roleKey === 'admin' ? 'primary' : undefined" :outlined="roleKey !== 'admin'">{{ roleLabel }}</v-chip>
          <v-chip v-if="channelRole" small outlined class="mr-1 mb-1">{{ channelRole }} in diesem Kanal</v-chip>
        </div>
        <v-alert v-if="locked" type="warning" outlined dense icon="mdi-lock" class="mb-3">
          Geschützt: niemand kann {{ person.clientNickname }} hier verschieben, anstupsen, rauswerfen oder bannen.
        </v-alert>

        <div class="actions">
          <v-btn outlined @click="open('message')"><v-icon left>mdi-message-text-outline</v-icon>Nachricht</v-btn>
          <v-btn outlined :disabled="locked" @click="open('poke')"><v-icon left>mdi-hand-wave-outline</v-icon>Anstupsen</v-btn>
          <v-btn outlined :disabled="locked" @click="open('move')"><v-icon left>mdi-swap-horizontal</v-icon>Verschieben</v-btn>
          <v-btn outlined :disabled="locked" @click="open('kickChannel')"><v-icon left>mdi-exit-run</v-icon>Aus dem Kanal</v-btn>
          <v-btn outlined color="error" :disabled="locked" @click="open('kickServer')"><v-icon left>mdi-logout</v-icon>Vom Server</v-btn>
          <v-btn outlined color="error" :disabled="locked" @click="open('ban')"><v-icon left>mdi-cancel</v-icon>Bannen</v-btn>
        </div>
      </v-card-text>
      <v-card-actions class="px-4 pb-4 pt-0">
        <v-btn text color="primary" :to="{ name: 'user', params: { cldbid: String(person.clientDatabaseId) } }">
          <v-icon left>mdi-account-key</v-icon>Rolle & Rechte von {{ person.clientNickname }}
        </v-btn>
      </v-card-actions>
    </template>

    <template v-else>
      <v-card-title>
        Nicht mehr online
        <v-spacer></v-spacer>
        <v-btn icon small aria-label="Schließen" @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text>Diese Person hat den Server verlassen.</v-card-text>
    </template>

    <!-- Aktion -->
    <v-dialog v-model="dialog.open" max-width="460">
      <v-card v-if="person">
        <v-card-title>{{ texts[dialog.mode].title }}</v-card-title>
        <v-card-text>
          <p class="text-body-2">{{ texts[dialog.mode].hint }}</p>
          <v-autocomplete
            v-if="dialog.mode === 'move'"
            v-model="dialog.cid"
            :items="channelItems"
            item-text="name"
            item-value="cid"
            label="In welchen Kanal?"
            outlined
            dense
            no-data-text="Kein Kanal gefunden"
          ></v-autocomplete>
          <v-select
            v-if="dialog.mode === 'ban'"
            v-model="dialog.time"
            :items="durations"
            label="Wie lange?"
            outlined
            dense
          ></v-select>
          <v-textarea
            v-if="dialog.mode === 'message'"
            v-model="dialog.text"
            label="Nachricht"
            outlined
            auto-grow
            rows="3"
            counter="1024"
            maxlength="1024"
            autofocus
          ></v-textarea>
          <v-text-field
            v-else-if="dialog.mode !== 'move'"
            v-model="dialog.text"
            :label="texts[dialog.mode].field"
            outlined
            dense
            :counter="texts[dialog.mode].max"
            :maxlength="texts[dialog.mode].max"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog.open = false">Abbrechen</v-btn>
          <v-btn
            depressed
            :color="texts[dialog.mode].danger ? 'error' : 'primary'"
            :loading="busy"
            :disabled="!canSend"
            @click="send"
            >{{ texts[dialog.mode].button }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { serverRoleOf, channelRoleLabel, channelPaths, errorText } from "@/iconic/rechte";
import { BAN_DURATIONS, since } from "@/iconic/live";

const flag = (v) => v === true || v === 1 || v === "1";

export default {
  props: {
    person: Object,
    clid: String,
    groups: Object,
    channels: Array,
    config: Object,
  },
  data() {
    return {
      busy: false,
      durations: BAN_DURATIONS,
      dialog: { open: false, mode: "message", text: "", cid: null, time: 3600 },
      texts: {
        message: { title: "Nachricht schreiben", hint: "Kommt als private Nachricht im TeamSpeak an, Absender «Web-Admin».", button: "Senden" },
        poke: { title: "Anstupsen", hint: "Erscheint bei der Person als Popup.", field: "Text (kurz)", max: 100, button: "Anstupsen" },
        move: { title: "Verschieben", hint: "Die Person landet sofort im gewählten Kanal.", button: "Verschieben" },
        kickChannel: { title: "Aus dem Kanal werfen", hint: "Die Person landet im Standardkanal und kann zurück.", field: "Grund (sieht die Person, freiwillig)", max: 40, button: "Rauswerfen", danger: true },
        kickServer: { title: "Vom Server werfen", hint: "Die Verbindung wird getrennt. Die Person kann gleich wieder rein.", field: "Grund (sieht die Person, freiwillig)", max: 40, button: "Vom Server werfen", danger: true },
        ban: { title: "Bannen", hint: "Die Person fliegt raus und kommt für die gewählte Zeit nicht mehr rein. Aufheben geht in der Bann-Liste.", field: "Grund (freiwillig)", max: 80, button: "Bannen", danger: true },
      },
    };
  },
  computed: {
    locked() {
      return this.person && this.config.protectedDbIds.includes(String(this.person.clientDatabaseId));
    },
    role() {
      return this.person ? serverRoleOf(this.groups, (this.person.clientServergroups || []).map(String)) : null;
    },
    roleKey() {
      return this.role ? this.role.key : "";
    },
    roleLabel() {
      return this.role ? this.role.label : "";
    },
    channelRole() {
      if (!this.person || String(this.person.clientChannelGroupId) === this.groups.defaultCg) return "";
      return channelRoleLabel(this.groups, this.person.clientChannelGroupId);
    },
    channelItems() {
      return channelPaths(this.channels).map((c) => ({ cid: c.cid, name: c.path }));
    },
    channelName() {
      const c = this.channels.find((x) => String(x.cid) === String(this.person.cid));
      return c ? c.channelName : "?";
    },
    status() {
      const p = this.person;
      if (flag(p.clientAway)) return "abwesend";
      if (flag(p.clientOutputMuted)) return "hört nichts";
      if (flag(p.clientInputMuted)) return "Mikrofon aus";
      return "online";
    },
    onlineSince() {
      return since(this.person.clientLastconnected);
    },
    canSend() {
      const m = this.dialog.mode;
      if (m === "message" || m === "poke") return Boolean(String(this.dialog.text || "").trim());
      if (m === "move") return Boolean(this.dialog.cid) && String(this.dialog.cid) !== String(this.person.cid);
      return true;
    },
  },
  methods: {
    open(mode) {
      this.dialog = { open: true, mode, text: "", cid: null, time: 3600 };
    },
    async send() {
      const clid = this.person.clid;
      const { mode, text, cid, time } = this.dialog;
      const msg = String(text || "").trim();
      const ts = this.$TeamSpeak;
      const name = this.person.clientNickname;
      this.busy = true;
      try {
        if (mode === "message") await ts.execute("sendtextmessage", { targetmode: 1, target: clid, msg });
        if (mode === "poke") await ts.execute("clientpoke", { clid, msg });
        if (mode === "move") await ts.execute("clientmove", { clid, cid });
        if (mode === "kickChannel") await ts.execute("clientkick", { clid, reasonid: 4, ...(msg ? { reasonmsg: msg } : {}) });
        if (mode === "kickServer") await ts.execute("clientkick", { clid, reasonid: 5, ...(msg ? { reasonmsg: msg } : {}) });
        if (mode === "ban") await ts.execute("banclient", { clid, time, ...(msg ? { banreason: msg } : {}) });
        const done = {
          message: "Nachricht gesendet.",
          poke: `${name} angestupst.`,
          move: `${name} verschoben.`,
          kickChannel: `${name} aus dem Kanal geworfen.`,
          kickServer: `${name} vom Server geworfen.`,
          ban: `${name} gebannt.`,
        }[mode];
        this.$toast.success(done);
        this.dialog.open = false;
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.busy = false;
    },
  },
};
</script>

<style scoped>
.actions {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
}
.actions .v-btn {
  justify-content: flex-start;
}
.channel-link {
  text-decoration: underline;
}
</style>
